import { Locales } from '@griply/types';
import { legacyReminderTimeToReminder } from '@griply/utils';
import { useCallback, useMemo, useState } from 'react';
import { Frequency, RRule } from 'rrule';
import { IconFormNames } from 'shared/assets/icons';
import { useToday } from 'shared/contexts/today';
import { useNlpRegex } from 'shared/hooks/use-nlp-regex';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import {
  CompletionTargetUnit,
  FrequencyUnit,
  RruleReferenceDate,
} from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Person } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask, NewSubTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { isNumber } from 'shared/utils/is-number';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { processNewTaskVariablesInString } from 'shared/utils/nlp/process-new-task-variables-in-string';
import { processTaskPropertiesFromString } from 'shared/utils/nlp/process-task-properties-from-string';
import { removeNlpPropertiesFromString } from 'shared/utils/nlp/remove-nlp-properties-from-string';
import { v4 as uuidv4 } from 'uuid';

export type Options = {
  nlpString: string;
  updateNlpString: (name: string) => void;
  lifeAreas: LifeArea[];
  goals: (Goal | NewGoalWithID)[];
  tags: (Tag | NewTagWithID)[];
  people?: Person[];
  language: Locales;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  createHabitAllowed: boolean;
  scheduleTargetAllowed: boolean;
  dateAnchored?: boolean;
};

export const useNewTaskState = (
  initialProperties: Partial<CreateTask> = {},
  {
    nlpString,
    updateNlpString,
    lifeAreas,
    goals,
    tags,
    people,
    language,
    dateFormat,
    timeFormat,
    createHabitAllowed,
    scheduleTargetAllowed,
    dateAnchored,
  }: Options,
) => {
  const today = useToday();
  const locale = useI18nLocale();
  const [description, updateDescription] = useState('');
  const [subTasks, setSubTasks] = useState<NewSubTask[]>([]);
  const [changedProps, setChangedProps] = useState<(keyof CreateTask)[]>([]);
  const [unfinishedSubtask, setUnfinishedSubtask] = useState<string>('');
  const [hideOnHabitList, setHideOnHabitList] = useState(!createHabitAllowed);
  const [hideOnTodo, setHideOnTodo] = useState(false);
  const [completionsPerDay, setCompletionsPerDay] = useState(1);
  const [scheduleEndDate, setScheduleEndDate] = useState<Timestamp | null>(
    null,
  );
  const [iconName, setIconName] = useState<IconFormNames>(IconFormNames.Repeat);

  const [ignoredWords, updateIgnoredWords] = useState<ID[]>([]);
  const [wordIds, updateWordIds] = useState<WordId[]>([]);

  const nlpRegex = useNlpRegex({ language, people });

  const task = useMemo(() => {
    // filter the initialProperties to only include those that are not changed
    const filteredInitialProperties = Object.entries(initialProperties).reduce<
      Partial<CreateTask>
    >((acc, [key, value]) => {
      if (changedProps.includes(key as keyof CreateTask)) {
        return acc;
      }
      return {
        ...acc,
        [key]: value,
      };
    }, {});

    // get the base of the task
    const result: CreateTask = {
      ...filteredInitialProperties,
      // process the words into a name without the connected words
      name: removeNlpPropertiesFromString(nlpString, {
        nlpRegex,
        wordIds,
        ignoredWordIds: ignoredWords,
        language,
        dateFormat,
        createHabitAllowed,
        scheduleTargetAllowed,
      }).trim(),
      description,
      type:
        filteredInitialProperties.type ??
        filteredInitialProperties?.schedules?.length
          ? createHabitAllowed
            ? TaskType.Habit
            : TaskType.Repeating
          : TaskType.Task,
      goalId: filteredInitialProperties?.goalId ?? null,
      lifeAreaId: filteredInitialProperties?.lifeAreaId ?? null,
      sectionId: filteredInitialProperties?.sectionId ?? null,
      tagIds: filteredInitialProperties.tagIds ?? null,
      reminderTime: filteredInitialProperties?.reminderTime ?? null,
      reminders: filteredInitialProperties?.reminders ?? null,
      parentIds: filteredInitialProperties?.parentIds ?? null,
      priority: filteredInitialProperties?.priority ?? null,
      schedules: filteredInitialProperties?.schedules ?? null,
      iconName,
    };

    const properties = processTaskPropertiesFromString(nlpString, {
      language,
      dateFormat,
      ignoredWordIds: ignoredWords,
      wordIds,
      goals,
      lifeAreas,
      tags,
      createHabitAllowed,
      scheduleTargetAllowed,
      people: [],
      dateAnchored,
    });

    Object.keys(properties).forEach((key) => {
      const _key = key as keyof typeof properties;

      if (_key === 'goal') {
        result.goalId = properties[_key] ?? null;
      }

      if (_key === 'lifeArea') {
        result.lifeAreaId = properties[_key] ?? null;
      }

      if (_key === 'tags') {
        result.tagIds = properties[_key] ?? null;
      }

      if (_key === 'deadline') {
        result.deadlineDeadline = properties[_key] ?? null;
      }

      if (_key === 'priority') {
        result.priority = properties[_key] ?? null;
      }

      if (_key === 'reminderTime') {
        const reminderOffset = properties[_key];
        result.reminders = isNumber(reminderOffset)
          ? [legacyReminderTimeToReminder(reminderOffset, uuidv4())]
          : null;
      }

      // habit / repeating
      if (properties.target || properties.rrule) {
        result.hideOnTodo = hideOnTodo;
        result.type =
          createHabitAllowed && !hideOnHabitList
            ? TaskType.Habit
            : TaskType.Repeating;
        result.endStrategy = null;
        result.deadlineDeadline = null;

        if (_key === 'target') {
          const timeslot =
            isNumber(properties.startTime) || isNumber(properties.duration)
              ? {
                  startTime: properties.startTime ?? null,
                  duration: properties.duration ?? null,
                }
              : filteredInitialProperties.timeslot ?? null;
          const frequency = properties.target?.unit!;

          result.schedules = [
            {
              entries: [],
              timeslot,
              startDate: properties.dueDate ?? today,
              endDate: scheduleEndDate,
              frequency: {
                count: properties.target?.count!,
                unit: properties.target?.unit!,
                label: properties.target?.label ?? null,
              },
              rrule: {
                format: new RRule({
                  freq:
                    frequency === FrequencyUnit.Month
                      ? Frequency.MONTHLY
                      : Frequency.DAILY,
                }).toString(),
                referenceDate: RruleReferenceDate.StartDate,
              },
              completionTarget: {
                unit: CompletionTargetUnit.Day,
                count: 1,
              },
            },
          ];
        }

        if (_key === 'rrule') {
          const timeslot =
            isNumber(properties.startTime) || isNumber(properties.duration)
              ? {
                  startTime: properties.startTime ?? null,
                  duration: properties.duration ?? null,
                }
              : filteredInitialProperties.timeslot ?? null;

          result.schedules = [
            {
              entries: [],
              timeslot,
              startDate: properties.dueDate ?? today,
              endDate: scheduleEndDate,
              frequency: null,
              rrule: {
                format: properties.rrule!,
                referenceDate: RruleReferenceDate.StartDate,
              },
              completionTarget: {
                unit: CompletionTargetUnit.Day,
                count: completionsPerDay,
              },
            },
          ];
        }
      }

      // regular task
      if (!(properties.target || properties.rrule)) {
        result.type = TaskType.Task;
        result.schedules = null;

        if (_key === 'dueDate') {
          result.endStrategy = {
            deadline: properties[_key],
            completionCount: null,
          };
        }

        if (_key === 'startTime' || _key === 'duration') {
          // Preserve the field NLP didn't touch — e.g. typing "1h30m" should
          // update only `duration` and keep the calendar-anchored `startTime`.
          const startTimeFromNlp = 'startTime' in properties;
          const durationFromNlp = 'duration' in properties;
          result.timeslot = {
            startTime: startTimeFromNlp
              ? properties.startTime ?? null
              : filteredInitialProperties.timeslot?.startTime ?? null,
            duration: durationFromNlp
              ? properties.duration ?? null
              : filteredInitialProperties.timeslot?.duration ?? null,
          };
        }
      }
    });

    return result;
  }, [
    changedProps,
    completionsPerDay,
    createHabitAllowed,
    dateAnchored,
    dateFormat,
    description,
    goals,
    hideOnHabitList,
    hideOnTodo,
    iconName,
    ignoredWords,
    initialProperties,
    language,
    lifeAreas,
    nlpRegex,
    nlpString,
    scheduleEndDate,
    scheduleTargetAllowed,
    tags,
    today,
    wordIds,
  ]);

  const updateTaskVariables = useCallback(
    (taskVariables: Partial<CreateTask>) => {
      setChangedProps((prev) =>
        Array.from(
          new Set<keyof CreateTask>([
            ...prev,
            ...(Object.keys(taskVariables) as (keyof CreateTask)[]),
          ]),
        ),
      );

      updateNlpString(
        processNewTaskVariablesInString(nlpString, taskVariables, {
          goals,
          lifeAreas,
          tags,
          language: locale,
          dateFormat,
          timeFormat,
          wordIds,
          ignoredWordIds: ignoredWords,
          createHabitAllowed,
          today,
        }),
      );
      if ('description' in taskVariables) {
        updateDescription(taskVariables.description ?? '');
      }
      if ('hideOnTodo' in taskVariables) {
        setHideOnTodo(!!taskVariables.hideOnTodo);
      }
      if (taskVariables.type === TaskType.Repeating) {
        setHideOnHabitList(true);
      }
      if (taskVariables.type === TaskType.Habit) {
        setHideOnHabitList(false);
      }
      if ('schedules' in taskVariables) {
        const schedule = taskVariables.schedules?.[0];
        if (schedule && schedule.rrule) {
          setCompletionsPerDay(schedule.completionTarget.count);
        }
        setScheduleEndDate(schedule?.endDate ?? null);
      }
      if ('iconName' in taskVariables) {
        setIconName(taskVariables.iconName ?? IconFormNames.Repeat);
      }
    },
    [
      createHabitAllowed,
      dateFormat,
      goals,
      locale,
      ignoredWords,
      lifeAreas,
      nlpString,
      tags,
      timeFormat,
      today,
      updateNlpString,
      wordIds,
    ],
  );

  const reset = useCallback(() => {
    setChangedProps([]);
    updateDescription('');
    setHideOnTodo(false);
    setHideOnHabitList(!createHabitAllowed);
    setCompletionsPerDay(1);
    setScheduleEndDate(null);
    setSubTasks([]);
    setUnfinishedSubtask('');
  }, [createHabitAllowed]);

  return {
    updateWordIds,
    updateIgnoredWords,
    task,
    subTasks,
    updateSubTasks: setSubTasks,
    newSubTask: unfinishedSubtask,
    updateNewSubTask: setUnfinishedSubtask,
    reset,
    updateTaskVariables,
  };
};
